import { useState, useEffect, useCallback } from 'react';
import { formatDate } from '../lib/dates';
import { api, ApiError } from '../lib/api';
import { DataTable, type Column } from '../components/DataTable';

/* ============================================================================
   ADMISSIONS — the pipeline a principal actually cares about, because it's how
   new money comes in.

     Enquiry → Application → [Assessment] → Offer → Enrolled

   Assessment is OPTIONAL and configured per school: a primary school doesn't
   interview a five-year-old; an engineering college always tests.
   ========================================================================== */

interface Admission {
  id: string; reference: string; institutionId: string;
  applicantName: string; dateOfBirth: string | null; gender: string | null; seekingClass: string;
  guardianName: string; guardianMobile: string; guardianEmail: string | null;
  address: string | null; previousSchool: string | null; notes: string | null;
  stage: string; source: string; isClosed: boolean;
  assessmentDate: string | null; assessmentScore: number | null; assessmentNote: string | null;
  offeredAt: string | null; offerExpiresOn: string | null;
  enrolledStudentId: string | null; closedReason: string | null;
  /** Set when this is a RE-ADMISSION — the person's earlier student record. */
  previousStudentId: string | null;
  createdAt: string;
  events: { stage: string; note: string; at: string }[];
}
interface Funnel {
  enquiries: number; applications: number; assessments: number; offers: number;
  enrolled: number; rejected: number; withdrawn: number;
  totalOpen: number; conversionPercent: number;
}
interface Settings {
  requireAssessment: boolean; publicFormEnabled: boolean;
  applicationFeePaise: number; offerValidDays: number; referencePrefix: string;
}

const STAGES = ['Enquiry', 'Application', 'Assessment', 'Offer', 'Enrolled', 'Rejected', 'Withdrawn'];

export function AdmissionsPage() {
  const [rows, setRows] = useState<Admission[] | null>(null);
  const [funnel, setFunnel] = useState<Funnel | null>(null);
  const [settings, setSettings] = useState<Settings | null>(null);
  const [stage, setStage] = useState<string>('');
  const [active, setActive] = useState<Admission | null>(null);
  const [adding, setAdding] = useState(false);

  const load = useCallback(() => {
    setRows(null);
    const q = stage ? `?stage=${stage}` : '';
    api.get<Admission[]>(`/api/admissions${q}`).then(setRows).catch(() => setRows([]));
    api.get<Funnel>('/api/admissions/funnel').then(setFunnel).catch(() => {});
    api.get<Settings>('/api/admissions/settings').then(setSettings).catch(() => {});
  }, [stage]);
  useEffect(load, [load]);

  const columns: Column<Admission>[] = [
    {
      id: 'reference', header: 'Reference', sortable: true,
      value: (a) => a.reference,
      cell: (a) => <span className="mono" style={{ fontSize: 12 }}>{a.reference}</span>,
    },
    {
      id: 'applicantName', header: 'Applicant', primary: true, sortable: true,
      value: (a) => a.applicantName,
      cell: (a) => a.applicantName,
    },
    {
      id: 'seekingClass', header: 'Seeking', sortable: true,
      value: (a) => a.seekingClass, cell: (a) => a.seekingClass,
    },
    {
      id: 'guardian', header: 'Guardian', hideMd: true,
      value: (a) => `${a.guardianName} ${a.guardianMobile}`,
      cell: (a) => (
        <>{a.guardianName}<span style={{ color: 'var(--ink-3)' }}> · {a.guardianMobile}</span></>
      ),
    },
    {
      id: 'source', header: 'From', hideSm: true,
      value: (a) => a.source,
      cell: (a) => (
        <span className="pill pill-muted" style={{ fontSize: 11 }}>
          {a.source === 'Website' ? '🌐 Website' : a.source}
        </span>
      ),
    },
    {
      id: 'stage', header: 'Stage', sortable: true,
      value: (a) => a.stage,
      cell: (a) => <StagePill stage={a.stage} />,
    },
    {
      id: 'open', header: '', actions: true,
      cell: (a) => (
        <button className="btn" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => setActive(a)}>
          Open
        </button>
      ),
    },
  ];

  return (
    <div>
      <div className="page-head">
        <div>
          <div className="eyebrow" style={{ marginBottom: 6 }}>Growth</div>
          <h1 className="h-display" style={{ fontSize: 28 }}>Admissions</h1>
        </div>
        <div className="page-head-actions">
          <button className="btn btn-accent" onClick={() => setAdding(true)}>+ New enquiry</button>
        </div>
      </div>

      {/* ---- the funnel: the number a principal asks for ---- */}
      {funnel && (
        <div className="kpis">
          <FunnelCard label="Open" value={funnel.totalOpen} hint="still in the pipeline" />
          <FunnelCard label="Offers out" value={funnel.offers} hint="awaiting acceptance" />
          <FunnelCard label="Enrolled" value={funnel.enrolled} hint="became students" />
          <FunnelCard label="Conversion" value={`${funnel.conversionPercent}%`} hint="enquiry → enrolled" />
        </div>
      )}

      {settings && !settings.publicFormEnabled && (
        <div className="pill pill-warn" style={{ padding: '8px 12px', borderRadius: 8, marginBottom: 12, justifyContent: 'flex-start' }}>
          The public enquiry form is currently <strong>switched off</strong>. Families can't apply from your website.
        </div>
      )}

      <DataTable
        rows={rows}
        columns={columns}
        rowKey={(a) => a.id}
        search
        searchPlaceholder="Search by name, guardian, mobile or reference…"
        onRowClick={(a) => setActive(a)}
        filters={
          <>
            <button className={`chip ${stage === '' ? 'is-on' : ''}`} onClick={() => setStage('')}>All</button>
            {STAGES.map((s) => (
              <button key={s} className={`chip ${stage === s ? 'is-on' : ''}`} onClick={() => setStage(s)}>
                {s}
              </button>
            ))}
          </>
        }
        empty={{
          title: 'No admissions yet',
          body: 'Enquiries from your website appear here automatically. You can also add a walk-in.',
          action: <button className="btn btn-accent" onClick={() => setAdding(true)}>+ New enquiry</button>,
        }}
      />

      {adding && <EnquiryDialog onClose={() => setAdding(false)} onDone={() => { setAdding(false); load(); }} />}
      {active && (
        <AdmissionDrawer
          admission={active}
          requireAssessment={settings?.requireAssessment ?? false}
          onClose={() => setActive(null)}
          onChanged={() => { setActive(null); load(); }}
        />
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */

function StagePill({ stage }: { stage: string }) {
  const cls =
    stage === 'Enrolled' ? 'pill-ok'
      : stage === 'Rejected' || stage === 'Withdrawn' ? 'pill-danger'
        : stage === 'Offer' ? 'pill-warn'
          : 'pill-muted';
  return <span className={`pill ${cls}`} style={{ fontSize: 11 }}>{stage}</span>;
}

function FunnelCard({ label, value, hint }: { label: string; value: number | string; hint: string }) {
  return (
    <div className="card" style={{ padding: 16 }}>
      <div style={{ fontSize: 11, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '.04em' }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 700, fontFamily: 'var(--font-display)' }}>{value}</div>
      <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>{hint}</div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */

function EnquiryDialog({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [institutions, setInstitutions] = useState<{ id: string; name: string }[]>([]);
  const [f, setF] = useState({
    institutionId: '', applicantName: '', seekingClass: '', guardianName: '',
    guardianMobile: '', guardianEmail: '', previousSchool: '', notes: '',
  });
  const [dupes, setDupes] = useState<Admission[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.get<{ id: string; name: string }[]>('/api/institutions')
      .then((r) => { setInstitutions(r); if (r.length === 1) setF((p) => ({ ...p, institutionId: r[0].id })); })
      .catch(() => {});
  }, []);

  const save = async () => {
    if (!f.institutionId || !f.applicantName.trim() || !f.seekingClass.trim()
      || !f.guardianName.trim() || !f.guardianMobile.trim()) return;
    setBusy(true); setError(null);
    try {
      const res = await api.post<{ admission: Admission; possibleDuplicates: Admission[] }>(
        '/api/admissions', { ...f, source: 'WalkIn' });

      // The clerk needs to SEE that this family may already have applied — before they
      // walk away with a second reference number.
      if (res.possibleDuplicates.length > 0) { setDupes(res.possibleDuplicates); return; }
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not save the enquiry.');
    } finally { setBusy(false); }
  };

  if (dupes.length > 0) {
    return (
      <div className="sheet-backdrop" onClick={onDone}>
        <div className="sheet" onClick={(e) => e.stopPropagation()}>
          <h2 className="h-display" style={{ fontSize: 19, marginBottom: 6 }}>Saved — but check this</h2>
          <p style={{ color: 'var(--ink-2)', fontSize: 14, marginBottom: 14 }}>
            This mobile number already has an open admission. It may be the same family who applied
            online — or a sibling, which is perfectly normal.
          </p>
          {dupes.map((d) => (
            <div key={d.id} className="card" style={{ padding: 12, marginBottom: 8 }}>
              <strong>{d.applicantName}</strong>
              <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>
                {d.reference} · {d.seekingClass} · <StagePill stage={d.stage} />
              </div>
            </div>
          ))}
          <button className="btn btn-accent" style={{ width: '100%', justifyContent: 'center' }} onClick={onDone}>
            Got it
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <div className="sheet" onClick={(e) => e.stopPropagation()}>
        <h2 className="h-display" style={{ fontSize: 20, marginBottom: 14 }}>New enquiry</h2>
        {error && <div className="pill pill-danger" style={{ padding: '8px 12px', borderRadius: 8, marginBottom: 12, justifyContent: 'flex-start' }}>{error}</div>}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div>
            <label className="label">Institution</label>
            <select className="input" value={f.institutionId} onChange={(e) => setF({ ...f, institutionId: e.target.value })}>
              <option value="">Select…</option>
              {institutions.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
            </select>
          </div>
          <div className="form-row">
            <div><label className="label">Applicant's name</label>
              <input className="input" value={f.applicantName} onChange={(e) => setF({ ...f, applicantName: e.target.value })} /></div>
            <div><label className="label">Seeking class</label>
              <input className="input" placeholder="Class 1 / CSE Sem 1" value={f.seekingClass} onChange={(e) => setF({ ...f, seekingClass: e.target.value })} /></div>
          </div>
          <div className="form-row">
            <div><label className="label">Guardian's name</label>
              <input className="input" value={f.guardianName} onChange={(e) => setF({ ...f, guardianName: e.target.value })} /></div>
            <div><label className="label">Mobile</label>
              <input className="input" value={f.guardianMobile} onChange={(e) => setF({ ...f, guardianMobile: e.target.value })} /></div>
          </div>
          <div><label className="label">Email (optional)</label>
            <input className="input" type="email" value={f.guardianEmail} onChange={(e) => setF({ ...f, guardianEmail: e.target.value })} /></div>
          <div><label className="label">Previous school (optional)</label>
            <input className="input" value={f.previousSchool} onChange={(e) => setF({ ...f, previousSchool: e.target.value })} /></div>
          <div><label className="label">Notes</label>
            <input className="input" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></div>
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-accent" disabled={busy} onClick={save} style={{ marginLeft: 'auto' }}>
            {busy ? 'Saving…' : 'Save enquiry'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */

function AdmissionDrawer({ admission: a, requireAssessment, onClose, onChanged }: {
  admission: Admission; requireAssessment: boolean;
  onClose: () => void; onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [enrolling, setEnrolling] = useState(false);

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true); setError(null);
    try { await fn(); onChanged(); }
    catch (e) { setError(e instanceof ApiError ? e.message : 'That didn’t work.'); }
    finally { setBusy(false); }
  };

  const nextLabel =
    a.stage === 'Enquiry' ? 'Move to Application'
      : a.stage === 'Application' ? (requireAssessment ? 'Move to Assessment' : 'Make an offer')
        : a.stage === 'Assessment' ? 'Make an offer'
          : null;

  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <div className="sheet" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 4 }}>
          <h2 className="h-display" style={{ fontSize: 21 }}>{a.applicantName}</h2>
          <StagePill stage={a.stage} />
        </div>
        <p className="mono" style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 16 }}>{a.reference}</p>

        {error && <div className="pill pill-danger" style={{ padding: '8px 12px', borderRadius: 8, marginBottom: 12, justifyContent: 'flex-start' }}>{error}</div>}

        <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '8px 14px', fontSize: 14, marginBottom: 18 }}>
          <Field label="Seeking">{a.seekingClass}</Field>
          <Field label="Guardian">{a.guardianName} · {a.guardianMobile}</Field>
          {a.guardianEmail && <Field label="Email">{a.guardianEmail}</Field>}
          {a.previousSchool && <Field label="Previously">{a.previousSchool}</Field>}
          <Field label="From">{a.source}</Field>
          {a.assessmentScore !== null && <Field label="Assessment">Score {a.assessmentScore}</Field>}
          {a.offerExpiresOn && <Field label="Offer expires">{formatDate(a.offerExpiresOn)}</Field>}
          {a.closedReason && <Field label="Reason">{a.closedReason}</Field>}
        </dl>

        {/* ---- has this child been here before? ---- */}
        {!a.isClosed && <ReadmissionPanel admission={a} onChanged={onChanged} />}

        {/* ---- history: what the school reads back to a parent who rings ---- */}
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', marginBottom: 8 }}>History</div>
        <div style={{ marginBottom: 20 }}>
          {a.events.map((e, i) => (
            <div key={i} style={{ display: 'flex', gap: 10, fontSize: 13, padding: '6px 0', borderBottom: '1px solid var(--line-2)' }}>
              <span style={{ color: 'var(--ink-2)' }}>{e.note}</span>
              <span className="mono" style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--ink-3)' }}>
                {formatDate(e.at)}
              </span>
            </div>
          ))}
        </div>

        {/* ---- actions ---- */}
        {!a.isClosed && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {a.stage === 'Assessment' && (
              <button className="btn" disabled={busy} onClick={() => {
                const score = window.prompt('Assessment score (leave blank if not scored):');
                if (score === null) return;
                void act(() => api.post(`/api/admissions/${a.id}/assessment`, {
                  date: new Date().toISOString().slice(0, 10),
                  score: score.trim() ? Number(score) : null,
                  note: null,
                }));
              }}>Record result</button>
            )}

            {nextLabel && (
              <button className="btn btn-accent" disabled={busy}
                onClick={() => void act(() => api.post(`/api/admissions/${a.id}/advance`, { note: null }))}>
                {nextLabel}
              </button>
            )}

            {a.stage === 'Offer' && (
              <button className="btn btn-accent" disabled={busy} onClick={() => setEnrolling(true)}>
                Enrol as student
              </button>
            )}

            <button className="btn" disabled={busy} style={{ marginLeft: 'auto' }} onClick={() => {
              const reason = window.prompt('Why is this being rejected? (the family may ask)');
              if (!reason?.trim()) return;
              void act(() => api.post(`/api/admissions/${a.id}/reject`, { reason }));
            }}>Reject</button>

            <button className="btn" disabled={busy} onClick={() => {
              const reason = window.prompt('Reason for withdrawal:') ?? 'Withdrawn';
              void act(() => api.post(`/api/admissions/${a.id}/withdraw`, { reason }));
            }}>Withdraw</button>
          </div>
        )}

        {a.stage === 'Enrolled' && (
          <div className="pill pill-ok" style={{ padding: '10px 12px', borderRadius: 8, justifyContent: 'flex-start' }}>
            ✓ Enrolled — this applicant is now a student.
          </div>
        )}

        {enrolling && (
          <EnrolDialog admission={a} onClose={() => setEnrolling(false)} onDone={onChanged} />
        )}
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <dt style={{ color: 'var(--ink-3)', fontSize: 12, textTransform: 'uppercase', letterSpacing: '.03em' }}>{label}</dt>
      <dd style={{ margin: 0 }}>{children}</dd>
    </>
  );
}

/* -------------------------------------------------------------------------- */

function EnrolDialog({ admission: a, onClose, onDone }: {
  admission: Admission; onClose: () => void; onDone: () => void;
}) {
  const [campuses, setCampuses] = useState<{ id: string; name: string }[]>([]);
  const [campusId, setCampusId] = useState('');
  const [admissionNumber, setAdmissionNumber] = useState('');
  const [classLabel, setClassLabel] = useState(a.seekingClass);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.get<{ id: string; name: string }[]>('/api/campuses')
      .then((r) => { setCampuses(r); if (r.length === 1) setCampusId(r[0].id); })
      .catch(() => {});
  }, []);

  const enrol = async () => {
    if (!campusId || !admissionNumber.trim()) return;
    setBusy(true); setError(null);
    try {
      await api.post(`/api/admissions/${a.id}/enrol`, {
        campusId, admissionNumber: admissionNumber.trim(), classLabel: classLabel.trim() || null,
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not enrol.');
    } finally { setBusy(false); }
  };

  return (
    <div className="sheet-backdrop" onClick={onClose} style={{ zIndex: 80 }}>
      <div className="sheet" onClick={(e) => e.stopPropagation()}>
        <h2 className="h-display" style={{ fontSize: 19, marginBottom: 4 }}>Enrol {a.applicantName}</h2>
        <p style={{ color: 'var(--ink-2)', fontSize: 13.5, marginBottom: 16 }}>
          This creates a real student record. It can only be done once.
        </p>

        {error && <div className="pill pill-danger" style={{ padding: '8px 12px', borderRadius: 8, marginBottom: 12, justifyContent: 'flex-start' }}>{error}</div>}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div><label className="label">Campus</label>
            <select className="input" value={campusId} onChange={(e) => setCampusId(e.target.value)}>
              <option value="">Select…</option>
              {campuses.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div><label className="label">Admission number</label>
            {/* ⚠ NOT "DPS1042". DPS is Delhi Public School — a real chain, on every other school's
                screen and in our own marketing frames until somebody read one. */}
            <input className="input mono" placeholder="2026-0142" value={admissionNumber}
              onChange={(e) => setAdmissionNumber(e.target.value)} /></div>
          <div><label className="label">Class</label>
            <input className="input" value={classLabel} onChange={(e) => setClassLabel(e.target.value)} /></div>
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-accent" disabled={busy || !campusId || !admissionNumber.trim()}
            onClick={enrol} style={{ marginLeft: 'auto' }}>
            {busy ? 'Enrolling…' : 'Create student'}
          </button>
        </div>
      </div>
    </div>
  );
}

interface FormerStudent {
  id: string; admissionNumber: string; fullName: string; classLabel: string | null;
  status: string; exitDate: string | null; exitReason: string | null;
}

/**
 * "Has this child been here before?"
 *
 * The step a school office currently does from memory. Getting it wrong creates a second,
 * disconnected record for the same child — which is how a family ends up with two fee ledgers and
 * a leaving certificate that references the wrong admission number.
 *
 * ⚠ LINKS, NEVER MERGES. The returning student gets a NEW record with a NEW admission number. The
 * old one keeps its own fees, results, attendance and certificates exactly as they were. This just
 * records that the two are the same person, so Profile 360 can show both.
 */
function ReadmissionPanel({ admission: a, onChanged }: { admission: Admission; onChanged: () => void }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(a.applicantName);
  const [results, setResults] = useState<FormerStudent[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const search = async () => {
    if (query.trim().length < 2) return;
    setBusy(true); setError(null);
    try { setResults(await api.get<FormerStudent[]>(`/api/admissions/former-students?search=${encodeURIComponent(query.trim())}`)); }
    catch (e) { setError(e instanceof ApiError ? e.message : 'Could not search.'); }
    finally { setBusy(false); }
  };

  const link = async (studentId: string) => {
    setBusy(true); setError(null);
    try {
      await api.post(`/api/admissions/${a.id}/readmission`, { previousStudentId: studentId });
      onChanged();
    } catch (e) { setError(e instanceof ApiError ? e.message : 'Could not link.'); }
    finally { setBusy(false); }
  };

  if (a.previousStudentId) {
    return (
      <div className="card" style={{ padding: 12, marginBottom: 18, background: 'var(--surface-2, transparent)' }}>
        <div style={{ fontSize: 13 }}>
          <strong>Returning student.</strong> Linked to their earlier record — on enrolment they’ll
          get a new admission number, and both periods will show on their profile.
        </div>
      </div>
    );
  }

  return (
    <div style={{ marginBottom: 18 }}>
      {!open ? (
        <button className="btn" style={{ fontSize: 12, padding: '5px 10px' }} onClick={() => setOpen(true)}>
          Studied here before?
        </button>
      ) : (
        <div className="card" style={{ padding: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', marginBottom: 8 }}>
            Find their earlier record
          </div>

          {error && <div className="pill pill-danger" style={{ padding: '8px 12px', borderRadius: 8, marginBottom: 10, justifyContent: 'flex-start' }}>{error}</div>}

          <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
            <input className="input" value={query} onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && void search()}
              placeholder="Name or old admission number" />
            <button className="btn" disabled={busy} onClick={() => void search()}>Search</button>
          </div>

          {results !== null && results.length === 0 && (
            <p style={{ fontSize: 12.5, color: 'var(--ink-3)' }}>
              No former student matched. If they’ve never been here, carry on with the admission as
              normal.
            </p>
          )}

          {results !== null && results.length > 0 && (
            <div style={{ display: 'grid', gap: 6 }}>
              {results.map((s) => (
                <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, padding: '6px 0' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <strong>{s.fullName}</strong>{' '}
                    <span className="mono" style={{ fontSize: 11, color: 'var(--ink-3)' }}>{s.admissionNumber}</span>
                    <div style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>
                      {s.status}
                      {s.classLabel ? ` · last in ${s.classLabel}` : ''}
                      {s.exitDate ? ` · left ${formatDate(s.exitDate)}` : ''}
                    </div>
                  </div>
                  <button className="btn" style={{ fontSize: 12, padding: '4px 10px' }}
                    disabled={busy} onClick={() => void link(s.id)}>
                    This is them
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* ⚠ Says what linking does and does not do. A clerk who thinks this merges the records
              will expect the old fees to carry over, and be alarmed when they don't. */}
          <p style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 10, lineHeight: 1.6 }}>
            Linking keeps the two enrolments separate — the earlier fees and results stay on the old
            record. It just means both periods show up on the student’s profile.
          </p>
        </div>
      )}
    </div>
  );
}
